import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, User, FileText,
  PoundSterling, Scale, Loader2, Shield, Building2,
  Plus, Trash2, Link2, ClipboardCheck, AlertTriangle, Undo2
} from "lucide-react";

import { LOGO_URL } from "../config/constants";
import { currentUserEmail } from "../utils/currentUserEmail";
import { FEE_EARNERS } from "../config/roleConfig";

const MATTER_TYPES = [
  { value: "purchase_resi", label: "Residential Purchase", code: "001" },
  { value: "sale_resi", label: "Residential Sale", code: "001" },
  { value: "remortgage", label: "Remortgage", code: "001" },
  { value: "remortgage_toe", label: "Remortgage + Transfer of Equity", code: "001" },
  { value: "toe_only", label: "Transfer of Equity Only", code: "001" },
  { value: "auction_review", label: "Auction Pack Review", code: "001" },
  { value: "declaration_trust", label: "Declaration of Trust", code: "001" },
  { value: "restrictive_covenant", label: "Restrictive Covenant", code: "001" },
  { value: "licence_to_assign", label: "Licence to Assign", code: "001" },
  { value: "licence_alterations", label: "Licence for Alterations", code: "001" },
  { value: "title_split", label: "Title Split", code: "001" },
  { value: "contracts", label: "Contracts (Commercial)", code: "002" },
  { value: "apa_purchase", label: "APA - Business / Asset Purchase", code: "002" },
  { value: "spa_purchase", label: "SPA - Share Purchase (Buyer)", code: "002" },
  { value: "spa_sale", label: "SPA - Share Sale (Seller)", code: "002" },
  { value: "ila", label: "Independent Legal Advice (ILA)", code: "002" },
  { value: "mbi", label: "Management Buy-In (MBI)", code: "002" },
  { value: "mbo", label: "Management Buy-Out (MBO)", code: "002" },
  { value: "shareholders_cross_option", label: "Shareholders Agreement & Deed of Cross-Option", code: "002" },
  { value: "comm_lease_assgn_surr", label: "Commercial Lease - Assignment / Surrender", code: "003" },
  { value: "comm_freehold_purchase", label: "Commercial Freehold Purchase", code: "003" },
  { value: "comm_freehold_sale", label: "Commercial Freehold Sale", code: "003" },
  { value: "comm_lease_landlord", label: "Commercial Lease - Landlord", code: "003" },
  { value: "comm_lease_tenant", label: "Commercial Lease - Tenant", code: "003" },
  { value: "comm_lease_assignment", label: "Commercial Lease - Assignment", code: "003" },
  { value: "comm_lease_surrender", label: "Commercial Lease - Surrender", code: "003" },
  { value: "disputes", label: "Dispute (Contentious)", code: "006" },
  { value: "ip_trademark", label: "Trademark", code: "007" },
  { value: "ip_copyright", label: "Copyright", code: "007" },
  { value: "ip_patent", label: "Patent", code: "007" }
];

const FIELD_VISIBILITY = {
  Q8_property_address: ["purchase_resi", "sale_resi", "comm_lease_assgn_surr", "remortgage", "remortgage_toe", "toe_only", "declaration_trust", "auction_review", "restrictive_covenant", "licence_to_assign", "licence_alterations", "title_split"],
  Q8_seller_name: ["purchase_resi", "apa_purchase"],
  Q8_buyer_name: ["sale_resi", "spa_purchase"],
  Q8_tenant_name: ["comm_lease_assgn_surr"],
  Q8_landlord_name: ["comm_lease_assgn_surr"],
  Q8_target_company: ["spa_purchase", "spa_sale", "mbi", "mbo"],
  Q8_seller_business_name: ["apa_purchase"],
  Q8_new_lender: ["remortgage", "remortgage_toe"],
  Q8_existing_lender: ["remortgage", "remortgage_toe"],
  Q8_new_owner: ["toe_only", "remortgage_toe"],
  Q8_auction_house: ["auction_review"],
  Q8_company_name_shareholders: ["shareholders_cross_option"],
  Q8_current_owner_name: ["declaration_trust"]
};

const STEPS = [
  { id: 1, title: "Client & Matter", icon: User },
  { id: 2, title: "Details & Parties", icon: Building2 },
  { id: 3, title: "Fees & Scope", icon: PoundSterling },
  { id: 4, title: "Risk & Review", icon: Shield }
];

export default function PreCaseMinder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Dashboard pipeline tracker links matters at `pre_open` / `ccl_draft` stages
  // here as `/pre-caseminder?matter={ref}&tab=ccl`. The page itself is a
  // registration form, but we surface a header banner offering "Edit FORF"
  // (and a link to the matter detail) so users on a post-FORF matter can
  // still navigate back to /forf/{ref} — that's where the Revert to Draft
  // button lives.
  const matterRefFromQuery = (searchParams.get("matter") || "").trim();
  const [onSharePoint, setOnSharePoint] = useState(null); // null = not chosen, true/false
  // null = not answered, true = yes on file, false = no (must complete fresh RA)
  const [riskAssessmentOnFile, setRiskAssessmentOnFile] = useState(null);
  // inline RA fields (only used when riskAssessmentOnFile === false)
  const [riskNarrative, setRiskNarrative] = useState("");
  const [mitigatingActions, setMitigatingActions] = useState("");
  const [riskAssessmentComplete, setRiskAssessmentComplete] = useState(false);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    Q1: "", Q2: "", Q3: "", Q4: "", Q5: "",
    Q6: "", Q7: "None", Q9: "",
    sharepoint_folder_url: "",
    Q8_property_address: "", Q8_seller_name: "", Q8_buyer_name: "",
    Q8_tenant_name: "", Q8_landlord_name: "", Q8_target_company: "",
    Q8_seller_business_name: "", Q8_new_lender: "", Q8_existing_lender: "",
    Q8_new_owner: "", Q8_auction_house: "",
    Q8_company_name_shareholders: "", Q8_current_owner_name: "",
    Q10: "",
    Q13: "Fixed fee", Q14: "", Q15: "50% on outset, 50% on exchange/completion (default)",
    Q15_other: "", Q16: "",
    Q18_estimated_timescale: "",
    Q28: "", Q29: "", Q30: "No", Q30_details: "",
    Q32A: "Yes – use standard wording", Q32C: "", Q32D: "",
    other_side_parties: []
  });

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const isVisible = (field) => !FIELD_VISIBILITY[field] || FIELD_VISIBILITY[field].includes(formData.Q9);

  const addParty = () => {
    set("other_side_parties", [
      ...formData.other_side_parties,
      { party_type: "Individual", legal_name: "", known_role: "" }
    ]);
  };

  const updateParty = (idx, field, value) => {
    const parties = [...formData.other_side_parties];
    parties[idx] = { ...parties[idx], [field]: value };
    set("other_side_parties", parties);
  };

  const removeParty = (idx) => {
    set("other_side_parties", formData.other_side_parties.filter((_, i) => i !== idx));
  };

  const canProceed = () => {
    if (step === 1) return formData.Q1 && formData.Q3 && formData.Q4 && formData.Q6 && formData.Q9;
    return true;
  };

  const handleCompleteRiskAssessment = () => {
    if (!riskNarrative.trim() || !mitigatingActions.trim()) return;
    setRiskAssessmentComplete(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...formData, form_type: "pre_caseminder" };
      // Tag whether SharePoint folder needs creation
      if (onSharePoint === false) {
        payload.needs_sharepoint_creation = true;
      }
      // Risk assessment fields
      payload.risk_assessment_on_file = riskAssessmentOnFile;
      if (riskAssessmentOnFile === false) {
        payload.risk_narrative = riskNarrative.trim();
        payload.mitigating_actions = mitigatingActions.trim();
      }
      delete payload.other_side_parties;
      const response = await axios.post(`${API}/submissions`, { data: payload });
      const submissionId = response.data.id;
      
      // Create conflict check if parties exist
      if (formData.other_side_parties.length > 0) {
        try {
          await axios.post(`${API}/conflict-checks`, {
            submission_id: submissionId,
            client_name: formData.Q1,
            client_type: "Individual",
            other_side_parties: formData.other_side_parties,
            conflict_status: "pending_review",
            decision_maker_name: formData.Q6,
            // BM-H4498: the check records who registered the matter.
            submitted_by: currentUserEmail() || null,
            decision_notes: "Pre-CaseMinder matter - conflict check created on registration"
          });
        } catch (e) {
          console.error("Conflict check creation failed:", e);
        }
      }

      // BM-H4541: the "create folder" branch called an endpoint that was never
      // built and promised a golden rule pipeline that does not exist. An older
      // matter keeps its own number; its steps are recorded, not re-run.

      toast.success("Pre-CaseMinder matter registered successfully!");
      navigate(`/submission/${submissionId}`);
    } catch (error) {
      toast.error(errorMessage(error, "Failed to register matter"));
    } finally {
      setSubmitting(false);
    }
  };

  const matterTypeLabel = MATTER_TYPES.find(m => m.value === formData.Q9)?.label || "";

  // Determine whether the main step form should be visible
  // (SharePoint answered AND risk question answered AND if RA needed, it's been completed)
  const showStepForm = onSharePoint !== null &&
    riskAssessmentOnFile !== null &&
    (riskAssessmentOnFile === true || riskAssessmentComplete);

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header */}
      <nav className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Bonsai Law" className="w-10 h-10" />
              <div>
                <h1 className="text-lg font-bold text-teal">Pre-CaseMinder Registration</h1>
                <p className="text-xs text-slate-500">Register existing matters for CCL &amp; conflict checking</p>
              </div>
            </div>
            <Button variant="ghost" onClick={() => navigate("/")} data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
          </div>
        </div>
      </nav>

      {/* Edit FORF banner (shown when arriving from the dashboard with ?matter=) */}
      {matterRefFromQuery && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div
            className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            data-testid="pre-caseminder-matter-banner"
          >
            <div className="text-sm text-amber-900">
              <span className="font-semibold">Looking for matter</span>{" "}
              <span className="font-mono" data-testid="pre-caseminder-matter-ref">
                {matterRefFromQuery}
              </span>
              ? You can re-open the FORF and revert it to draft from there.
            </div>
            <Button
              variant="outline"
              onClick={() => navigate(`/forf/${encodeURIComponent(matterRefFromQuery)}`)}
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
              data-testid="pre-caseminder-edit-forf-btn"
            >
              <Undo2 className="w-4 h-4 mr-2" /> Edit FORF
            </Button>
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="bg-teal-dim border border-teal rounded-md p-4 mb-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-teal mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-teal">This form registers pre-existing matters into CaseMinder</p>
              <p className="text-xs text-teal mt-1">No LawBillity entries or notification emails will be generated. The matter will be available for FORF, CCL generation, and conflict checking.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── BRANCHING: SharePoint ── */}
      {onSharePoint === null ? (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="glass-card shadow-sm p-6 sm:p-8">
            <h2 className="text-xl font-semibold text-teal mb-2">Before we begin</h2>
            <p className="text-sm text-slate-500 mb-6">Does this matter already have a folder on SharePoint?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => setOnSharePoint(true)}
                className="group flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-slate-200 hover:border-teal hover:bg-teal/5 transition-all text-center"
                data-testid="branch-yes-sharepoint"
              >
                <Link2 className="w-8 h-8 text-teal group-hover:scale-110 transition-transform" />
                <span className="font-semibold text-slate-700">Yes — already on SharePoint</span>
                <span className="text-xs text-slate-400">I'll paste the existing SharePoint folder URL and assign a reference</span>
              </button>
              <button
                onClick={() => setOnSharePoint(false)}
                className="group flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-slate-200 hover:border-teal hover:bg-teal/5 transition-all text-center"
                data-testid="branch-no-sharepoint"
              >
                <FileText className="w-8 h-8 text-teal group-hover:scale-110 transition-transform" />
                <span className="font-semibold text-slate-700">No — create folder for me</span>
                <span className="text-xs text-slate-400">Register it now; create its folder in SharePoint under the same matter number</span>
              </button>
            </div>
          </div>
        </div>

      /* ── BRANCHING: Risk Assessment on file? ── */
      ) : riskAssessmentOnFile === null ? (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="glass-card shadow-sm p-6 sm:p-8">
            <h2 className="text-xl font-semibold text-teal mb-2">Risk Assessment</h2>
            <p className="text-sm text-slate-500 mb-6">Is there already a risk assessment on file for this matter?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => setRiskAssessmentOnFile(true)}
                className="group flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-slate-200 hover:border-teal hover:bg-teal/5 transition-all text-center"
                data-testid="branch-yes-risk-assessment"
              >
                <ClipboardCheck className="w-8 h-8 text-teal group-hover:scale-110 transition-transform" />
                <span className="font-semibold text-slate-700">Yes — risk assessment on file</span>
                <span className="text-xs text-slate-400">This will be recorded against the matter. No further action needed here.</span>
              </button>
              <button
                onClick={() => setRiskAssessmentOnFile(false)}
                className="group flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-slate-200 hover:border-amber-500 hover:bg-status-amber-dim transition-all text-center"
                data-testid="branch-no-risk-assessment"
              >
                <AlertTriangle className="w-8 h-8 text-status-amber group-hover:scale-110 transition-transform" />
                <span className="font-semibold text-slate-700">No — complete one now</span>
                <span className="text-xs text-slate-400">You'll need to complete a risk assessment before the matter can be registered</span>
              </button>
            </div>
            <div className="mt-4 text-center">
              <button
                onClick={() => { setRiskAssessmentOnFile(null); setOnSharePoint(null); }}
                className="text-xs text-slate-400 hover:text-slate-600 underline"
                data-testid="ra-back-to-sharepoint"
              >
                ← Back
              </button>
            </div>
          </div>
        </div>

      /* ── INLINE RISK ASSESSMENT (gate — must complete before proceeding) ── */
      ) : riskAssessmentOnFile === false && !riskAssessmentComplete ? (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="glass-card shadow-sm p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="w-6 h-6 text-status-amber flex-shrink-0" />
              <div>
                <h2 className="text-xl font-semibold text-slate-800">Risk Assessment Required</h2>
                <p className="text-sm text-slate-500 mt-0.5">Complete both fields below before you can proceed to register this matter.</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label className="text-sm font-medium text-slate-700">
                  Risk Narrative <span className="text-status-red">*</span>
                </Label>
                <p className="text-xs text-slate-400 mb-1.5">Identify and describe the material risks associated with this matter</p>
                <Textarea
                  value={riskNarrative}
                  onChange={e => setRiskNarrative(e.target.value)}
                  placeholder="e.g. Client is a PEP. Transaction involves offshore entities. Unusual payment route requested..."
                  rows={4}
                  data-testid="input-risk-narrative"
                  className="border-slate-300 focus:border-teal focus:ring-teal/20"
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">
                  Mitigating Actions <span className="text-status-red">*</span>
                </Label>
                <p className="text-xs text-slate-400 mb-1.5">What steps have been or will be taken to mitigate those risks?</p>
                <Textarea
                  value={mitigatingActions}
                  onChange={e => setMitigatingActions(e.target.value)}
                  placeholder="e.g. Enhanced due diligence completed. Source of funds documentation obtained. Partner sign-off obtained..."
                  rows={4}
                  data-testid="input-mitigating-actions"
                  className="border-slate-300 focus:border-teal focus:ring-teal/20"
                />
              </div>
            </div>

            <div className="flex items-center justify-between mt-6 pt-5 border-t">
              <button
                onClick={() => setRiskAssessmentOnFile(null)}
                className="text-xs text-slate-400 hover:text-slate-600 underline"
                data-testid="ra-form-back"
              >
                ← Back
              </button>
              <Button
                onClick={handleCompleteRiskAssessment}
                disabled={!riskNarrative.trim() || !mitigatingActions.trim()}
                className="bg-teal hover:bg-teal-hover text-white"
                data-testid="btn-complete-risk-assessment"
              >
                <Check className="w-4 h-4 mr-2" /> Risk Assessment Complete — Continue
              </Button>
            </div>
          </div>
        </div>

      /* ── MAIN STEP FORM ── */
      ) : (
      <>

      {/* Step Progress */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="flex items-center justify-between">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center flex-1">
              <div className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                step === s.id ? "bg-teal text-white" :
                step > s.id ? "bg-teal/20 text-teal" :
                "bg-slate-100 text-slate-400"
              }`}>
                <s.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{s.title}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${step > s.id ? "bg-teal" : "bg-slate-200"}`} />
              )}
            </div>
          ))}
        </div>
        {/* Risk assessment status pill */}
        <div className="mt-3 flex items-center gap-2">
          {riskAssessmentOnFile === true ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-teal-dim text-teal border border-teal">
              <ClipboardCheck className="w-3.5 h-3.5" /> Risk assessment on file
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-status-amber-dim text-status-amber border border-amber-200">
              <Check className="w-3.5 h-3.5" /> Risk assessment completed
            </span>
          )}
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="glass-card shadow-sm p-6 sm:p-8">

          {/* Step 1: Client & Matter Info */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1-client-matter">
              <h2 className="text-xl font-semibold text-teal mb-4">Client & Matter Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Client Name *</Label>
                  <Input value={formData.Q1} onChange={e => set("Q1", e.target.value)} placeholder="e.g., Smith & Co Ltd" data-testid="input-client-name" />
                </div>
                <div>
                  <Label>Matter Title *</Label>
                  <Input value={formData.Q3} onChange={e => set("Q3", e.target.value)} placeholder="e.g., Purchase of 10 High Street" data-testid="input-matter-title" />
                </div>
              </div>

              <div>
                <Label>Client Address</Label>
                <Textarea value={formData.Q2} onChange={e => set("Q2", e.target.value)} placeholder="Full postal address" rows={3} data-testid="input-client-address" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Our Reference *</Label>
                  <Input value={formData.Q4} onChange={e => set("Q4", e.target.value)} placeholder="e.g., BL.SM.001" data-testid="input-our-ref" />
                </div>
                <div>
                  <Label>Your Reference</Label>
                  <Input value={formData.Q5} onChange={e => set("Q5", e.target.value)} placeholder="Client's reference (optional)" data-testid="input-your-ref" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Fee Earner *</Label>
                  <Select value={formData.Q6} onValueChange={v => set("Q6", v)}>
                    <SelectTrigger data-testid="select-fee-earner"><SelectValue placeholder="Select fee earner" /></SelectTrigger>
                    <SelectContent>
                      {FEE_EARNERS.map(fe => (
                        <SelectItem key={fe.name} value={fe.name}>{fe.name} ({fe.position})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Matter Type *</Label>
                  <Select value={formData.Q9} onValueChange={v => set("Q9", v)}>
                    <SelectTrigger data-testid="select-matter-type"><SelectValue placeholder="Select matter type" /></SelectTrigger>
                    <SelectContent>
                      {MATTER_TYPES.map(mt => (
                        <SelectItem key={mt.value} value={mt.value}>{mt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {onSharePoint === true && (
              <div>
                <Label className="flex items-center gap-2"><Link2 className="w-4 h-4" /> SharePoint Folder URL</Label>
                <Input value={formData.sharepoint_folder_url} onChange={e => set("sharepoint_folder_url", e.target.value)} placeholder="https://bonsailaw.sharepoint.com/..." data-testid="input-sharepoint-url" />
                <p className="text-xs text-slate-400 mt-1">Paste the existing SharePoint folder link for this matter</p>
              </div>
              )}
              {onSharePoint === false && (
              <div className="bg-teal-dim border border-teal rounded-md p-3">
                <p className="text-xs text-teal">
                  <FileText className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  CaseMinder does not create a folder for an older matter. Create it in SharePoint under this
                  matter number, so the steps panel can find its documents.
                </p>
              </div>
              )}
            </div>
          )}

          {/* Step 2: Details & Other Side Parties */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-2-details-parties">
              <h2 className="text-xl font-semibold text-teal mb-4">Matter Details & Other Side</h2>
              
              {/* Matter-specific fields */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-txt-muted uppercase tracking-wider">Matter-Specific Details</h3>
                {formData.Q9 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {isVisible("Q8_property_address") && (
                      <div className="md:col-span-2">
                        <Label>Property Address</Label>
                        <Input value={formData.Q8_property_address} onChange={e => set("Q8_property_address", e.target.value)} data-testid="input-property-address" />
                      </div>
                    )}
                    {isVisible("Q8_seller_name") && (
                      <div><Label>Seller Name</Label><Input value={formData.Q8_seller_name} onChange={e => set("Q8_seller_name", e.target.value)} data-testid="input-seller-name" /></div>
                    )}
                    {isVisible("Q8_buyer_name") && (
                      <div><Label>Buyer Name</Label><Input value={formData.Q8_buyer_name} onChange={e => set("Q8_buyer_name", e.target.value)} data-testid="input-buyer-name" /></div>
                    )}
                    {isVisible("Q8_tenant_name") && (
                      <div><Label>Tenant Name</Label><Input value={formData.Q8_tenant_name} onChange={e => set("Q8_tenant_name", e.target.value)} data-testid="input-tenant-name" /></div>
                    )}
                    {isVisible("Q8_landlord_name") && (
                      <div><Label>Landlord Name</Label><Input value={formData.Q8_landlord_name} onChange={e => set("Q8_landlord_name", e.target.value)} data-testid="input-landlord-name" /></div>
                    )}
                    {isVisible("Q8_target_company") && (
                      <div><Label>Target Company</Label><Input value={formData.Q8_target_company} onChange={e => set("Q8_target_company", e.target.value)} data-testid="input-target-company" /></div>
                    )}
                    {isVisible("Q8_seller_business_name") && (
                      <div><Label>Seller Business Name</Label><Input value={formData.Q8_seller_business_name} onChange={e => set("Q8_seller_business_name", e.target.value)} data-testid="input-seller-business" /></div>
                    )}
                    {isVisible("Q8_new_lender") && (
                      <div><Label>New Lender</Label><Input value={formData.Q8_new_lender} onChange={e => set("Q8_new_lender", e.target.value)} data-testid="input-new-lender" /></div>
                    )}
                    {isVisible("Q8_existing_lender") && (
                      <div><Label>Existing Lender</Label><Input value={formData.Q8_existing_lender} onChange={e => set("Q8_existing_lender", e.target.value)} data-testid="input-existing-lender" /></div>
                    )}
                    {isVisible("Q8_new_owner") && (
                      <div><Label>New Owner</Label><Input value={formData.Q8_new_owner} onChange={e => set("Q8_new_owner", e.target.value)} data-testid="input-new-owner" /></div>
                    )}
                    {isVisible("Q8_auction_house") && (
                      <div><Label>Auction House</Label><Input value={formData.Q8_auction_house} onChange={e => set("Q8_auction_house", e.target.value)} data-testid="input-auction-house" /></div>
                    )}
                    {isVisible("Q8_company_name_shareholders") && (
                      <div><Label>Company Name</Label><Input value={formData.Q8_company_name_shareholders} onChange={e => set("Q8_company_name_shareholders", e.target.value)} data-testid="input-company-name" /></div>
                    )}
                    {isVisible("Q8_current_owner_name") && (
                      <div><Label>Current Owner</Label><Input value={formData.Q8_current_owner_name} onChange={e => set("Q8_current_owner_name", e.target.value)} data-testid="input-current-owner" /></div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">Select a matter type in Step 1 to see relevant fields</p>
                )}
              </div>

              <div>
                <Label>Internal Description / Notes</Label>
                <Textarea value={formData.Q10} onChange={e => set("Q10", e.target.value)} placeholder="Any internal notes about this matter..." rows={3} data-testid="input-internal-desc" />
              </div>

              {/* Other Side Parties */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-txt-muted uppercase tracking-wider">Other Side Parties (for Conflict Check)</h3>
                  <Button variant="outline" size="sm" onClick={addParty} data-testid="add-party-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Party
                  </Button>
                </div>
                
                {formData.other_side_parties.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No parties added. Click "Add Party" to enter opposing parties for conflict checking.</p>
                ) : (
                  <div className="space-y-3">
                    {formData.other_side_parties.map((party, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-navy-hover/60 rounded-md border" data-testid={`party-${idx}`}>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1">
                          <Select value={party.party_type} onValueChange={v => updateParty(idx, "party_type", v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Individual">Individual</SelectItem>
                              <SelectItem value="Company">Company</SelectItem>
                              <SelectItem value="Trust">Trust</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input value={party.legal_name} onChange={e => updateParty(idx, "legal_name", e.target.value)} placeholder="Legal name" data-testid={`party-name-${idx}`} />
                          <Input value={party.known_role} onChange={e => updateParty(idx, "known_role", e.target.value)} placeholder="Role (e.g., Seller)" />
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeParty(idx)} className="text-status-red hover:text-status-red mt-1">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Fees & Scope */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-3-fees-scope">
              <h2 className="text-xl font-semibold text-teal mb-4">Fees, Scope & Timescales</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Pricing Model</Label>
                  <Select value={formData.Q13} onValueChange={v => set("Q13", v)}>
                    <SelectTrigger data-testid="select-pricing"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Fixed fee">Fixed fee</SelectItem>
                      <SelectItem value="Hourly rate">Hourly rate</SelectItem>
                      <SelectItem value="Fixed fee + hourly if exceeded">Fixed fee + hourly if exceeded</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fee Quote</Label>
                  <Input value={formData.Q14} onChange={e => set("Q14", e.target.value)} placeholder="e.g., 2500" data-testid="input-fee-quote" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Fee Split</Label>
                  <Select value={formData.Q15} onValueChange={v => set("Q15", v)}>
                    <SelectTrigger data-testid="select-fee-split"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50% on outset, 50% on exchange/completion (default)">50/50 split (default)</SelectItem>
                      <SelectItem value="single_invoice">Single invoice on completion</SelectItem>
                      <SelectItem value="Other (specify below)">Other (specify)</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.Q15 === "Other (specify below)" && (
                    <Input className="mt-2" value={formData.Q15_other} onChange={e => set("Q15_other", e.target.value)} placeholder="Describe fee split..." />
                  )}
                </div>
                <div>
                  <Label>Disbursements Estimate</Label>
                  <Input value={formData.Q16} onChange={e => set("Q16", e.target.value)} placeholder="e.g., 500" data-testid="input-disbursements" />
                </div>
              </div>

              <div>
                <Label>Estimated Timescale (weeks)</Label>
                <Input value={formData.Q18_estimated_timescale} onChange={e => set("Q18_estimated_timescale", e.target.value)} placeholder="e.g., 8-12" data-testid="input-timescale" />
              </div>

              {/* Scope */}
              <div className="pt-4 border-t">
                <h3 className="text-sm font-medium text-txt-muted uppercase tracking-wider mb-4">Scope of Work</h3>
                <div className="space-y-4">
                  <RadioGroup value={formData.Q32A} onValueChange={v => set("Q32A", v)}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes – use standard wording" id="scope-std" />
                      <Label htmlFor="scope-std">Use standard scope wording for {matterTypeLabel || "this matter type"}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No – I will write bespoke scope" id="scope-custom" />
                      <Label htmlFor="scope-custom">Write bespoke scope</Label>
                    </div>
                  </RadioGroup>

                  {formData.Q32A === "No – I will write bespoke scope" && (
                    <Textarea value={formData.Q32D} onChange={e => set("Q32D", e.target.value)} placeholder="Enter bespoke scope of work..." rows={5} data-testid="input-scope-custom" />
                  )}
                  
                  <div>
                    <Label>Additional Scope Items (appended to standard)</Label>
                    <Textarea value={formData.Q32C} onChange={e => set("Q32C", e.target.value)} placeholder="Any additional scope items..." rows={3} data-testid="input-scope-additions" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Risk & Review */}
          {step === 4 && (
            <div className="space-y-6" data-testid="step-4-risk-review">
              <h2 className="text-xl font-semibold text-teal mb-4">Risk Assessment & Review</h2>

              {/* Show risk assessment summary (read-only) if already completed or on file */}
              {riskAssessmentOnFile === true ? (
                <div className="bg-teal-dim border border-teal rounded-md p-4 flex items-start gap-3">
                  <ClipboardCheck className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-teal">Risk assessment confirmed on file</p>
                    <p className="text-xs text-teal mt-0.5">Recorded at matter registration.</p>
                  </div>
                </div>
              ) : (
                <div className="bg-status-amber-dim border border-amber-200 rounded-md p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-status-amber flex-shrink-0 mt-0.5" />
                    <p className="text-sm font-medium text-status-amber">Risk assessment completed</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Risk Narrative</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{riskNarrative}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Mitigating Actions</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{mitigatingActions}</p>
                  </div>
                </div>
              )}
              
              <div>
                <Label>Material Risks (additional notes)</Label>
                <Textarea value={formData.Q28} onChange={e => set("Q28", e.target.value)} placeholder="Any additional material risks for this matter..." rows={3} data-testid="input-risks" />
              </div>

              <div>
                <Label>Key Deadline</Label>
                <Input value={formData.Q29} onChange={e => set("Q29", e.target.value)} placeholder="e.g., Exchange by 31 March 2026" data-testid="input-deadline" />
              </div>

              <div>
                <Label>Multiple Clients?</Label>
                <RadioGroup value={formData.Q30} onValueChange={v => set("Q30", v)}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="multi-no" />
                    <Label htmlFor="multi-no">No</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="multi-yes" />
                    <Label htmlFor="multi-yes">Yes</Label>
                  </div>
                </RadioGroup>
                {formData.Q30 === "Yes" && (
                  <Textarea className="mt-2" value={formData.Q30_details} onChange={e => set("Q30_details", e.target.value)} placeholder="Detail the multiple client arrangement..." rows={2} />
                )}
              </div>

              {/* Review Summary */}
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-lg font-semibold text-teal mb-4">Summary</h3>
                <div className="bg-navy-hover/60 rounded-md p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Client:</span><span className="font-medium">{formData.Q1 || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Matter:</span><span className="font-medium">{formData.Q3 || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Reference:</span><span className="font-medium">{formData.Q4 || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Type:</span><span className="font-medium">{matterTypeLabel || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Fee Earner:</span><span className="font-medium">{formData.Q6 || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Fee:</span><span className="font-medium">{formData.Q14 ? `£${formData.Q14}` : "—"} ({formData.Q13})</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Timescale:</span><span className="font-medium">{formData.Q18_estimated_timescale ? `${formData.Q18_estimated_timescale} weeks` : "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Other Side Parties:</span><span className="font-medium">{formData.other_side_parties.length}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Risk Assessment:</span><span className={`font-medium ${riskAssessmentOnFile ? "text-teal" : "text-status-amber"}`}>{riskAssessmentOnFile ? "On file" : "Completed"}</span></div>
                  {formData.sharepoint_folder_url && (
                    <div className="flex justify-between"><span className="text-slate-500">SharePoint:</span><span className="font-medium text-blue-600 truncate max-w-[200px]">Linked</span></div>
                  )}
                  {onSharePoint === false && (
                    <div className="flex justify-between"><span className="text-slate-500">SharePoint:</span><span className="font-medium text-status-amber">No folder yet</span></div>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  This will create a Pre-CaseMinder submission. No LawBillity entry or client notification emails will be sent.
                  {" The matter keeps this matter number. Next, open it and use \"Record steps done before CaseMinder\" to give the date of each golden rule step; CaseMinder looks in the matter's SharePoint folder for the documents. Do not start a new conflict check for it: that opens a new matter number and folder."}
                </p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <Button variant="outline" onClick={() => step > 1 ? setStep(step - 1) : setRiskAssessmentOnFile(null)} data-testid="btn-back">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            {step < 4 ? (
              <Button onClick={() => setStep(step + 1)} disabled={!canProceed()} className="bg-teal hover:bg-teal-hover text-white" data-testid="btn-next">
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting || !canProceed()} className="bg-teal hover:bg-teal-hover text-white" data-testid="btn-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Register Matter
              </Button>
            )}
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
